// src/lib/constants/captions.ts
//
// Live automated captions (AI live captioning) — constants for the caption
// term-set editor, the AI term-research (generate) flow, and the per-stream
// caption settings. Everything here mirrors the /v1/captions server contract;
// the server remains the validation authority.
// NOTE: this is the LIVE captions feature — unrelated to VOD caption files
// under features/media (CaptionsTab).

/** Term-set limits — client-side mirror of the server's validation. */
export const CAPTION_TERM_CONSTRAINTS = {
  /** Max terms per term set, counted after case-insensitive dedupe. */
  MAX_TERMS: 200,
  /** Max characters per term (server 400s and lists offenders past this). */
  TERM_MAX_LENGTH: 50,
} as const;

/**
 * Caption languages offered in stream settings. Fixed list of the caption
 * vendor's natively supported live languages — only English is offered at
 * launch; append entries here (value = wire code) as more are enabled.
 */
export const CAPTION_LANGUAGES = [
  { value: 'en', label: 'English' },
] as const;

export const CAPTION_LANGUAGE_VALUES = ['en'] as const;
export type CaptionLanguage = typeof CAPTION_LANGUAGE_VALUES[number];
export const DEFAULT_CAPTION_LANGUAGE: CaptionLanguage = 'en';

/** Caption render position on the player. */
export const CAPTION_POSITIONS = ['bottom', 'top'] as const;
export type CaptionPosition = typeof CAPTION_POSITIONS[number];
export const DEFAULT_CAPTION_POSITION: CaptionPosition = 'bottom';

export const CAPTION_POSITION_LABELS: Record<CaptionPosition, string> = {
  bottom: 'Bottom',
  top: 'Top',
} as const;

/** AI term-research (generate) request limits — mirror of the server contract. */
export const CAPTION_GENERATE_CONSTRAINTS = {
  ORG_NAME_MAX: 200,
  CONTEXT_NOTE_MAX: 300,
  SOURCE_URLS_MAX: 4,
  /** Server quota: generations per month (429 past this or while one runs). */
  MONTHLY_LIMIT: 10,
  /**
   * The api ALB idle timeout (60 s) drops the held generate response on every
   * real run (1–8 min), so the flow resolves by POLLING the generations list;
   * the POST's own network failure is expected, not an error.
   */
  GENERATION_POLL_MS: 25_000,
  /** Tolerance when matching a run's server-side created_epoch to client submit time. */
  SUBMIT_CLOCK_SKEW_SEC: 120,
  /** Give up polling after this long (runs observed ≤ 8 min). */
  GENERATION_GIVE_UP_SEC: 720,
} as const;

/**
 * Organization categories for the generate flow. Wire values are fixed
 * strings; 'other' swaps in a free-text category field. `urlHint` is the
 * Source URLs guidance shown for the selected category — URLs are the
 * highest-leverage research input, so the examples match the domain.
 */
export const CAPTION_GENERATE_CATEGORIES = [
  {
    value: 'sports',
    label: 'Sports',
    urlHint: 'Pages listing names — team rosters, schedules, league or conference pages.',
  },
  {
    value: 'church-ministry',
    label: 'Church / Ministry',
    urlHint: 'Pages listing names — staff and leadership, ministries, sermon series.',
  },
  {
    value: 'entertainment',
    label: 'Entertainment',
    urlHint: 'Pages listing names — cast and crew, program lineups, venue schedules.',
  },
  {
    value: 'meetings',
    label: 'Meetings',
    urlHint: 'Pages listing names — agendas, board and committee member lists.',
  },
  {
    value: 'education',
    label: 'Education',
    urlHint: 'Pages listing names — faculty and staff directories, departments, athletics rosters.',
  },
  {
    value: 'government',
    label: 'Government',
    urlHint: 'Pages listing names — council or board members, departments, meeting agendas.',
  },
  {
    value: 'other',
    label: 'Other',
    urlHint: 'Pages listing names and terms — rosters, staff pages, lineups, agendas.',
  },
] as const;

export const CAPTION_GENERATE_CATEGORY_VALUES = [
  'sports',
  'church-ministry',
  'entertainment',
  'meetings',
  'education',
  'government',
  'other',
] as const;
export type CaptionGenerateCategory = typeof CAPTION_GENERATE_CATEGORY_VALUES[number];

/** Query key factory for captions server state. */
export const CAPTIONS_QUERY_KEYS = {
  all: ['captions'] as const,
  termSets: (cId: number) => [...CAPTIONS_QUERY_KEYS.all, 'term-sets', cId] as const,
  generations: (cId: number) => [...CAPTIONS_QUERY_KEYS.all, 'generations', cId] as const,
} as const;
